import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useId, useState } from 'react';

import { formatAmount, formatCurrency } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type {
  GroupMembersReportDto,
  MemberContributionDto,
  MemberExpenseReportDto,
} from '@splitfiesta/shared';
import { Avatar } from '@splitfiesta/ui';

import { SplitFiestaLoading } from '@/components/common/SplitFiestaLoading';
import {
  HatchPattern,
  SECOND_SERIES_DASH,
  hatchSwatchStyle,
  squareDot,
} from '@/components/reports/chartSeries';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';

import {
  useGroupMembersReportQuery,
  useMemberExpenseReportQuery,
} from '@/hooks/queries/useReports';

import { resolveUserName } from '@/lib/displayName';

import { ReportBarList } from './ReportBarList';

interface MembersReportProps {
  groupId: string;
  startDate: string;
  endDate: string;
  currency: string;
}

export default function MembersReport({
  groupId,
  startDate,
  endDate,
  currency,
}: MembersReportProps) {
  const { t } = useTranslation('reports');
  useNamespace('expenses');
  useNamespace('common');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  // Fetch group members report (overview)
  const { data: membersReport, isLoading: isLoadingMembers } =
    useGroupMembersReportQuery(groupId, startDate, endDate);

  // Fetch individual member report (drill-down)
  const { data: memberReport, isLoading: isLoadingMember } =
    useMemberExpenseReportQuery(
      groupId,
      selectedMemberId ?? '',
      startDate,
      endDate,
      !!selectedMemberId
    );

  if (isLoadingMembers) {
    return (
      <div className="flex justify-center py-12">
        <SplitFiestaLoading className="min-h-0 bg-transparent" />
      </div>
    );
  }

  if (!membersReport) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-12">
        {t('members.noData')}
      </div>
    );
  }

  // If a member is selected, show their detailed report
  if (selectedMemberId && memberReport) {
    return (
      <MemberDetailView
        report={memberReport}
        currency={currency}
        onBack={() => setSelectedMemberId(null)}
        t={t}
        isLoading={isLoadingMember}
      />
    );
  }

  // Show loading while fetching member detail
  if (selectedMemberId && isLoadingMember) {
    return (
      <div className="flex justify-center py-12">
        <SplitFiestaLoading className="min-h-0 bg-transparent" />
      </div>
    );
  }

  // Show overview
  return (
    <MembersOverview
      report={membersReport}
      currency={currency}
      onSelectMember={setSelectedMemberId}
      t={t}
    />
  );
}

// ============= Overview Components =============

interface MembersOverviewProps {
  report: GroupMembersReportDto;
  currency: string;
  onSelectMember: (memberId: string) => void;
  t: (key: string) => string;
}

function MembersOverview({
  report,
  currency,
  onSelectMember,
  t,
}: MembersOverviewProps) {
  // Two reports on one page would otherwise share one pattern id.
  const hatchId = `hatch-benefited-${useId()}`;

  // Prepare chart data
  const chartData = report.members.map(m => ({
    name: resolveUserName(m.userName, t),
    userId: m.userId,
    paid: m.totalPaid,
    benefited: m.totalBenefited,
    net: m.netContribution,
  }));

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('activityTitle')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('activitySubtitle')}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-linear-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 px-4 py-4 rounded-lg min-w-0">
          <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">
            {t('members.overview.totalSpent')}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900 dark:text-blue-100 truncate">
            {formatCurrency(report.totals.totalSpent, currency)}
          </p>
        </div>
        <div className="bg-linear-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 px-4 py-4 rounded-lg min-w-0">
          <p className="text-sm text-green-600 dark:text-green-400 mb-1">
            {t('members.overview.expenseCount')}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-900 dark:text-green-100">
            {report.totals.expenseCount}
          </p>
        </div>
        <div className="bg-linear-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 px-4 py-4 rounded-lg min-w-0">
          <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">
            {t('members.overview.memberCount')}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-900 dark:text-purple-100">
            {report.totals.memberCount}
          </p>
        </div>
        <div className="bg-linear-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 px-4 py-4 rounded-lg min-w-0">
          <p className="text-sm text-orange-600 dark:text-orange-400 mb-1">
            {t('members.overview.avgPerMember')}
          </p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-900 dark:text-orange-100 truncate">
            {formatCurrency(
              report.totals.totalSpent / report.totals.memberCount || 0,
              currency
            )}
          </p>
        </div>
      </div>

      {/* Comparison Chart */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('members.overview.comparisonChart')}
        </h3>
        <div className="h-80 w-full min-w-0">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ left: 0, right: 0, top: 5, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#374151"
                opacity={0.3}
              />
              <XAxis
                type="number"
                tickFormatter={v => formatAmount(v)}
                stroke="#6b7280"
                tick={{ fontSize: 10 }}
              />
              <YAxis
                type="category"
                dataKey="name"
                width="auto"
                tick={{ fontSize: 10 }}
                stroke="#6b7280"
              />
              <Tooltip
                content={({ payload, label }) => (
                  <CustomBarTooltip
                    payload={payload as CustomBarTooltipProps['payload']}
                    label={label}
                    t={t}
                    currency={currency}
                  />
                )}
                allowEscapeViewBox={{ x: false, y: false }}
                wrapperStyle={{ zIndex: 50 }}
                cursor={false}
              />
              <defs>
                <HatchPattern id={hatchId} color="#3b82f6" />
              </defs>
              <Bar dataKey="paid" fill="#10b981" name={t('members.paid')} />
              <Bar
                dataKey="benefited"
                fill={`url(#${hatchId})`}
                name={t('members.spent')}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center gap-4 sm:gap-6 mt-4 text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-xs bg-emerald-500" />
            <span className="text-gray-600 dark:text-gray-300">
              {t('members.paid')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-xs"
              style={hatchSwatchStyle('#3b82f6')}
            />
            <span className="text-gray-600 dark:text-gray-300">
              {t('members.spent')}
            </span>
          </div>
        </div>
      </div>

      {/* Contribution Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('members.overview.contributionTable')}
        </h3>
        {/* Mobile Cards View */}
        <div className="block sm:hidden space-y-3">
          {report.members.map(member => (
            <MemberCard
              key={member.userId}
              member={member}
              currency={currency}
              onClick={() => onSelectMember(member.userId)}
              t={t}
            />
          ))}
        </div>
        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <th className="text-left py-3 px-4 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.table.member')}
                </th>
                <th className="text-right py-3 px-4 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.table.paid')}
                </th>
                <th className="text-right py-3 px-4 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.table.spent')}
                </th>
                <th className="text-right py-3 px-4 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.table.net')}
                </th>
                <th className="text-right py-3 px-4 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('members.table.share')}
                </th>
              </tr>
            </thead>
            <tbody>
              {report.members.map((member, index) => (
                <MemberRow
                  key={member.userId}
                  member={member}
                  currency={currency}
                  onClick={() => onSelectMember(member.userId)}
                  isEven={index % 2 === 0}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leaderboards */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('members.leaderboard.title')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <LeaderboardCard
            title={t('members.leaderboard.topPayers')}
            items={[...report.members]
              .sort((a, b) => b.totalPaid - a.totalPaid)
              .map(m => ({
                userId: m.userId,
                name: resolveUserName(m.userName, t),
                value: formatCurrency(m.totalPaid, currency),
              }))}
            onMemberClick={onSelectMember}
            t={t}
          />
          <LeaderboardCard
            title={t('members.leaderboard.topSpenders')}
            items={report.rankings.topBeneficiaries.map(b => ({
              userId: b.userId,
              name: resolveUserName(b.userName, t),
              value: formatCurrency(b.amount, currency),
            }))}
            onMemberClick={onSelectMember}
            t={t}
          />
          <LeaderboardCard
            title={t('members.leaderboard.topContributors')}
            items={report.rankings.topContributors.map(c => ({
              userId: c.userId,
              name: resolveUserName(c.userName, t),
              value: formatCurrency(c.amount, currency),
              amount: c.amount,
            }))}
            onMemberClick={onSelectMember}
            t={t}
          />
          <LeaderboardCard
            title={t('members.leaderboard.mostActive')}
            items={report.rankings.mostActive.map(a => ({
              userId: a.userId,
              name: resolveUserName(a.userName, t),
              value: `${a.count} ${t('members.expenses')}`,
            }))}
            onMemberClick={onSelectMember}
            t={t}
          />
        </div>
      </div>
    </div>
  );
}

// ============= Member Detail View =============

interface MemberDetailViewProps {
  report: MemberExpenseReportDto;
  currency: string;
  onBack: () => void;
  t: (key: string) => string;
  isLoading: boolean;
}

function MemberDetailView({
  report,
  currency,
  onBack,
  t,
  isLoading,
}: MemberDetailViewProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <SplitFiestaLoading className="min-h-0 bg-transparent" />
      </div>
    );
  }

  // Prepare category chart data
  // Largest first, like mobile; a category the member only benefited from
  // has nothing paid to chart.
  const categorySlices = report.categoryBreakdown
    .filter(c => c.totalPaid > 0)
    .sort((a, b) => b.totalPaid - a.totalPaid)
    .map(c => ({
      key: c.category,
      name: t(`expenses:categories.${c.category}`),
      value: c.totalPaid,
    }));

  // Prepare monthly trend data
  const trendData = [...report.monthlyTrend].reverse().map(m => ({
    month: m.month,
    paid: m.totalPaid,
    benefited: m.totalBenefited,
    net: m.netContribution,
  }));

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium text-sm sm:text-base"
      >
        <svg
          className="w-4 h-4 sm:w-5 sm:h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        {t('members.detail.backToOverview')}
      </button>

      {/* Member Header */}
      <div className="flex items-center gap-3 sm:gap-4">
        <Avatar
          name={report.memberName}
          src={report.memberProfileImage}
          size="lg"
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
            {resolveUserName(report.memberName, t)}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {report.startDate} {t('common:dateRange.to')} {report.endDate}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-linear-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 px-3 sm:px-4 py-3 sm:py-4 rounded-lg min-w-0">
          <p className="text-xs sm:text-sm text-green-600 dark:text-green-400 mb-1">
            {t('members.detail.totalPaid')}
          </p>
          <p className="text-base sm:text-lg lg:text-xl font-bold text-green-900 dark:text-green-100 truncate">
            {formatCurrency(report.summary.totalPaid, currency)}
          </p>
        </div>
        <div className="bg-linear-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 px-3 sm:px-4 py-3 sm:py-4 rounded-lg min-w-0">
          <p className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 mb-1">
            {t('members.detail.totalSpent')}
          </p>
          <p className="text-base sm:text-lg lg:text-xl font-bold text-blue-900 dark:text-blue-100 truncate">
            {formatCurrency(report.summary.totalBenefited, currency)}
          </p>
        </div>
        <div
          className={`bg-linear-to-br ${
            report.summary.netContribution >= 0
              ? 'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20'
              : 'from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20'
          } px-3 sm:px-4 py-3 sm:py-4 rounded-lg min-w-0`}
        >
          <p
            className={`text-xs sm:text-sm mb-1 ${
              report.summary.netContribution >= 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {t('members.detail.netContribution')}
          </p>
          <p
            className={`text-base sm:text-lg lg:text-xl font-bold truncate ${
              report.summary.netContribution >= 0
                ? 'text-green-900 dark:text-green-100'
                : 'text-red-900 dark:text-red-100'
            }`}
          >
            {formatCurrency(report.summary.netContribution, currency)}
          </p>
        </div>
        <div className="bg-linear-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 px-3 sm:px-4 py-3 sm:py-4 rounded-lg min-w-0">
          <p className="text-xs sm:text-sm text-orange-600 dark:text-orange-400 mb-1">
            {t('members.detail.generosityScore')}
          </p>
          <p className="text-base sm:text-lg lg:text-xl font-bold text-orange-900 dark:text-orange-100">
            {report.summary.generosityScore}%
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {/* Category Breakdown */}
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('members.detail.paymentsByCategory')}
          </h3>
          {categorySlices.length > 0 ? (
            <ReportBarList slices={categorySlices} currency={currency} />
          ) : (
            <div className="h-56 sm:h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
              {t('members.detail.noCategories')}
            </div>
          )}
        </div>

        {/* Monthly Trend */}
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('members.detail.monthlyTrend')}
          </h3>
          {trendData.length > 0 ? (
            <div className="h-56 sm:h-64 w-full min-w-0">
              <ResponsiveContainer width="100%" height={224}>
                <LineChart
                  data={trendData}
                  margin={{ left: 10, right: 10, top: 5, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    opacity={0.3}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10 }}
                    stroke="#6b7280"
                  />
                  <YAxis
                    tickFormatter={v => formatAmount(v)}
                    tick={{ fontSize: 9 }}
                    stroke="#6b7280"
                    width={70}
                  />
                  <Tooltip
                    formatter={(value: unknown) =>
                      formatCurrency(Number(value ?? 0), currency)
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="paid"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: '#10b981', r: 3 }}
                    name={t('members.paid')}
                  />
                  <Line
                    type="monotone"
                    dataKey="benefited"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    strokeDasharray={SECOND_SERIES_DASH}
                    dot={squareDot('#3b82f6', 6)}
                    name={t('members.spent')}
                  />
                </LineChart>
              </ResponsiveContainer>
              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full shrink-0 bg-emerald-500" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('members.paid')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-xs shrink-0 bg-blue-500" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('members.spent')}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-56 sm:h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
              {t('members.detail.noTrends')}
            </div>
          )}
        </div>
      </div>

      {/* Recent Expenses */}
      <div className="pt-8">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('members.detail.recentExpenses')}
        </h3>
        {report.recentExpenses.length > 0 ? (
          <div className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {report.recentExpenses.map(expense => (
              <div
                key={expense.id}
                className="px-3 sm:px-4 py-3 flex justify-between items-center gap-3 bg-white dark:bg-gray-900"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 dark:text-white text-sm sm:text-base truncate">
                    {expense.title}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {expense.date}
                    {expense.category &&
                      ` • ${t(`expenses:categories.${expense.category}`)}`}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                    <CurrencyDisplay
                      amount={expense.amount}
                      currency={currency}
                      original={
                        expense.originalAmount !== undefined &&
                        expense.originalCurrency !== undefined
                          ? {
                              amount: expense.originalAmount,
                              currency: expense.originalCurrency,
                            }
                          : undefined
                      }
                    />
                  </p>
                  <p
                    className={`text-xs sm:text-sm font-medium ${
                      expense.paidAmount - expense.benefitedAmount > 0
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : expense.paidAmount - expense.benefitedAmount < 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {expense.paidAmount - expense.benefitedAmount > 0 && '+'}
                    {formatCurrency(
                      expense.paidAmount - expense.benefitedAmount,
                      currency
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400 rounded-lg border border-gray-200 dark:border-gray-700">
            {t('members.detail.noExpenses')}
          </div>
        )}
      </div>
    </div>
  );
}

// ============= Helper Components =============

interface MemberCardProps {
  member: MemberContributionDto;
  currency: string;
  onClick: () => void;
  t: (key: string) => string;
}

function MemberCard({ member, currency, onClick, t }: MemberCardProps) {
  return (
    <div
      onClick={onClick}
      className="cursor-pointer bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
    >
      <div className="flex items-center gap-3 mb-3">
        <Avatar name={member.userName} src={member.profileImage} size="sm" />
        <span className="font-medium text-gray-900 dark:text-white truncate flex-1">
          {resolveUserName(member.userName, t)}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {member.percentageOfGroupSpending.toFixed(1)}%
        </span>
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-gray-500 dark:text-gray-400">
            {t('members.table.paid')}
          </span>
          <span className="font-medium text-green-600 dark:text-green-400">
            {formatCurrency(member.totalPaid, currency)}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-500 dark:text-gray-400">
            {t('members.table.spent')}
          </span>
          <span className="font-medium text-blue-600 dark:text-blue-400">
            {formatCurrency(member.totalBenefited, currency)}
          </span>
        </div>
        <div className="flex justify-between items-center pt-1.5 border-t border-gray-100 dark:border-gray-700">
          <span className="text-gray-500 dark:text-gray-400">
            {t('members.table.net')}
          </span>
          <span
            className={`font-semibold ${
              member.netContribution >= 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {member.netContribution >= 0 ? '+' : ''}
            {formatCurrency(member.netContribution, currency)}
          </span>
        </div>
      </div>
    </div>
  );
}

interface MemberRowProps {
  member: MemberContributionDto;
  currency: string;
  onClick: () => void;
  isEven: boolean;
}

function MemberRow({ member, currency, onClick, isEven }: MemberRowProps) {
  const { t } = useTranslation();
  return (
    <tr
      onClick={onClick}
      className={`cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors ${
        isEven ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'
      }`}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={member.userName} src={member.profileImage} size="sm" />
          <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
            {resolveUserName(member.userName, t)}
          </span>
        </div>
      </td>
      <td className="text-right py-3 px-4 text-green-600 dark:text-green-400 font-medium text-sm">
        {formatCurrency(member.totalPaid, currency)}
      </td>
      <td className="text-right py-3 px-4 text-blue-600 dark:text-blue-400 font-medium text-sm">
        {formatCurrency(member.totalBenefited, currency)}
      </td>
      <td
        className={`text-right py-3 px-4 font-medium text-sm ${
          member.netContribution >= 0
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400'
        }`}
      >
        {member.netContribution >= 0 ? '+' : ''}
        {formatCurrency(member.netContribution, currency)}
      </td>
      <td className="text-right py-3 px-4 text-gray-600 dark:text-gray-400 text-sm">
        {member.percentageOfGroupSpending.toFixed(1)}%
      </td>
    </tr>
  );
}

interface LeaderboardCardProps {
  title: string;
  items: Array<{
    userId: string;
    name: string;
    value: string;
    amount?: number; // Raw amount for coloring (green/red)
  }>;
  onMemberClick?: (userId: string) => void;
  t: (key: string) => string;
}

function LeaderboardCard({
  title,
  items,
  onMemberClick,
  t,
}: LeaderboardCardProps) {
  const [expanded, setExpanded] = useState(false);
  const displayItems = expanded ? items : items.slice(0, 3);
  const hasMore = items.length > 3;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
      <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h4>
      <div className="space-y-2">
        {displayItems.length > 0 ? (
          displayItems.map((item, index) => (
            <div
              key={item.userId}
              className="py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0"
            >
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs flex items-center justify-center font-medium shrink-0 mt-0.5">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => onMemberClick?.(item.userId)}
                    className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 leading-tight text-left hover:underline"
                  >
                    {item.name}
                  </button>
                  <p
                    className={`text-sm font-semibold mt-0.5 text-right ${
                      item.amount !== undefined
                        ? item.amount >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {item.value}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
            No data
          </p>
        )}
      </div>
      {hasMore && (
        <div className="mt-3 text-center">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium"
          >
            {expanded
              ? t('common:actions.showLess')
              : t('common:actions.seeAll')}
          </button>
        </div>
      )}
    </div>
  );
}

// Custom tooltip for bar chart
interface CustomBarTooltipProps {
  payload?: ReadonlyArray<{
    name?: string;
    value: number;
    color: string;
    dataKey: string;
  }>;
  label?: string | number;
  t: (key: string) => string;
  currency: string;
}

function CustomBarTooltip({
  payload,
  label,
  t,
  currency,
}: CustomBarTooltipProps) {
  if (payload && payload.length > 0) {
    return (
      <div className="bg-white dark:bg-gray-800 p-2 sm:p-3 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-w-[200px] sm:max-w-[280px] overflow-hidden">
        <p className="font-medium text-gray-900 dark:text-white mb-2 text-xs sm:text-sm truncate">
          {label}
        </p>
        {payload.map((entry, index) => (
          <div
            key={index}
            className="flex items-center justify-between gap-2 text-xs"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <div
                className="w-2.5 h-2.5 rounded-xs shrink-0"
                // The benefited bar's colour is a pattern url, not a colour.
                style={
                  entry.dataKey === 'benefited'
                    ? hatchSwatchStyle('#3b82f6')
                    : { backgroundColor: entry.color }
                }
              />
              <span className="text-gray-600 dark:text-gray-300">
                {entry.dataKey === 'paid'
                  ? t('members.paid')
                  : t('members.spent')}
              </span>
            </div>
            <span className="font-medium text-gray-900 dark:text-white shrink-0">
              {formatCurrency(entry.value, currency)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}
